import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { toast } from "sonner";
import { Eye, EyeSlash, CheckCircle, Warning, SpinnerGap, UserSwitch, SignOut, XCircle } from "phosphor-react";
import { bumpAuthVersion, hardAuthReset } from "../utils/authSignal";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * ActivatePage - Account activation / password setup for technicians
 * 
 * Route: /activate?token=...
 * 
 * SESSION GATE (P0 - prevents identity confusion):
 * - If logged in as a DIFFERENT email than the activation target: BLOCK
 * - User must explicitly switch accounts (logout + reload)
 * - Prevents contractor/other-tech seeing wrong dashboard after activation
 */
export default function ActivatePage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [errorReason, setErrorReason] = useState(null);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Session gate state
  const [sessionUser, setSessionUser] = useState(null);
  const [sessionMismatch, setSessionMismatch] = useState(false);
  const [switchingAccount, setSwitchingAccount] = useState(false);

  /**
   * Fetch current session user (if any)
   */
  const fetchSessionUser = useCallback(async () => {
    try {
      const authToken = localStorage.getItem("auth_token") || localStorage.getItem("token");
      const response = await fetch(`${API}/auth/me`, {
        credentials: "include",
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      });
      
      if (response.ok) {
        return await response.json();
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  /**
   * Compare emails case-insensitively
   */
  const emailsMatch = (email1, email2) => {
    if (!email1 || !email2) return false;
    return email1.toLowerCase().trim() === email2.toLowerCase().trim();
  };

  /**
   * Handle "Switch Account" - logout current session and reload activation page
   */
  const handleSwitchAccount = async () => {
    setSwitchingAccount(true);
    try {
      // 1. Bump auth version to notify other tabs BEFORE logout
      bumpAuthVersion('session_switch', null);
      
      // 2. Call backend logout
      await fetch(`${API}/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
      
      // 3. CRITICAL: Clear ALL cached data (auth + domain data)
      hardAuthReset();
      
      // 4. Hard reload to same URL to ensure clean identity hydration
      window.location.replace(window.location.href);
    } catch (err) {
      console.error("[ActivatePage] Switch account failed:", err);
      toast.error("Failed to switch account. Please try again.");
      setSwitchingAccount(false);
    }
  };

  // Validate token AND check session on mount
  useEffect(() => {
    if (!token) {
      setValidating(false);
      setErrorReason("No token provided");
      return;
    }

    const initPage = async () => {
      try {
        // Fetch token validation AND session user in parallel
        const [tokenResponse, currentUser] = await Promise.all([
          fetch(`${API}/auth/validate-token?token=${encodeURIComponent(token)}&token_type=login`),
          fetchSessionUser(),
        ]);
        
        const tokenData = await tokenResponse.json();
        
        if (tokenData.valid) {
          setTokenValid(true);
          setTokenInfo(tokenData);
          setSessionUser(currentUser);
          
          // SESSION GATE CHECK
          // If user is logged in AND emails don't match -> show gate
          if (currentUser && currentUser.email && tokenData.email) {
            if (!emailsMatch(currentUser.email, tokenData.email)) {
              setSessionMismatch(true);
            }
          }
        } else {
          setErrorReason(tokenData.reason === "expired" 
            ? "This link has expired. Please ask your contractor to send a new login email."
            : "This link is invalid. It may have already been used."
          );
        }
      } catch (error) {
        console.error("Token validation error:", error);
        setErrorReason("Unable to validate link. Please try again.");
      } finally {
        setValidating(false);
      }
    };

    initPage();
  }, [token, fetchSessionUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Block submission if session mismatch (safety check)
    if (sessionMismatch) {
      toast.error("Please switch accounts first");
      return;
    }

    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setSubmitting(true);
    try {
      // Include auth header if we have a session (for backend mismatch check)
      const authToken = localStorage.getItem("auth_token") || localStorage.getItem("token");
      const headers = { "Content-Type": "application/json" };
      if (authToken) {
        headers["Authorization"] = `Bearer ${authToken}`;
      }

      const response = await fetch(`${API}/auth/activate`, {
        method: "POST",
        headers,
        body: JSON.stringify({ token, password }),
      });

      const data = await response.json();

      if (response.ok) {
        toast.success("Account activated successfully!");
        
        // CRITICAL: Clear ALL old cached data (auth + domain data)
        hardAuthReset();
        
        // Store NEW token for the activated user
        if (data.token) {
          localStorage.setItem("auth_token", data.token);
        }
        
        // Bump auth version to notify other tabs BEFORE redirect
        // This causes other tabs (e.g. contractor dashboard) to purge and reset
        bumpAuthVersion('activate', { 
          email: data.user?.email,
          role: data.user?.role || 'technician'
        });
        
        // Force hard reload to tech portal for clean identity hydration
        // This ensures menu/identity comes from fresh /api/auth/me
        setTimeout(() => {
          window.location.replace("/tech");
        }, 1000);
      } else if (response.status === 409 && data.error === "SESSION_MISMATCH") {
        // Backend caught session mismatch
        toast.error(data.message || "Session mismatch detected. Please log out and try again.");
        setSessionMismatch(true);
        setSessionUser({ email: data.session_email });
      } else {
        toast.error(data.detail || "Failed to activate account");
      }
    } catch (error) {
      console.error("Activation error:", error);
      toast.error("Failed to activate account. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Loading state
  if (validating) {
    return (
      <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 text-center">
            <SpinnerGap size={48} className="animate-spin text-[#FF6600] mx-auto mb-4" />
            <p className="text-zinc-600">Validating your activation link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Warning size={48} className="text-amber-500 mx-auto mb-2" />
            <CardTitle className="text-xl">Link Invalid</CardTitle>
          </CardHeader>
          <CardContent className="text-center space-y-4">
            <p className="text-zinc-600">{errorReason}</p>
            <Button
              onClick={() => navigate("/")}
              variant="outline"
              className="mt-4"
            >
              Go to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ============================================
  // SESSION MISMATCH GATE (P0 - prevents identity confusion)
  // ============================================
  if (sessionMismatch && sessionUser && tokenInfo) {
    return (
      <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-2 border-amber-300" data-testid="session-mismatch-gate">
          <CardHeader className="text-center border-b border-amber-100 pb-6 bg-amber-50/50">
            <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
              <UserSwitch size={32} className="text-amber-600" />
            </div>
            <CardTitle className="text-xl font-heading">
              Different Account Detected
            </CardTitle>
            <CardDescription className="text-base mt-2 text-amber-800">
              You need to switch accounts to activate this account
            </CardDescription>
          </CardHeader>

          <CardContent className="py-6 space-y-6">
            {/* Current session info */}
            <div className="bg-zinc-100 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-zinc-500">Currently logged in as</span>
                <span className="font-medium text-zinc-800" data-testid="current-session-email">
                  {sessionUser.email}
                </span>
              </div>
              <div className="border-t border-zinc-200 pt-3 flex items-center justify-between">
                <span className="text-sm text-zinc-500">Activation is for</span>
                <span className="font-medium text-amber-700" data-testid="activation-email">
                  {tokenInfo.email}
                </span>
              </div>
            </div>

            {/* Warning message */}
            <Alert className="border-amber-300 bg-amber-50">
              <Warning className="h-5 w-5 text-amber-600" />
              <AlertDescription className="text-amber-800">
                To activate this account, you must log out of your current account first.
                Your activation link will remain valid.
              </AlertDescription>
            </Alert>

            {/* Actions */}
            <div className="flex flex-col gap-3 pt-2">
              <Button
                onClick={handleSwitchAccount}
                disabled={switchingAccount}
                className="w-full h-12 bg-amber-600 hover:bg-amber-700 text-white text-lg"
                data-testid="switch-account-btn"
              >
                {switchingAccount ? (
                  <>
                    <SpinnerGap className="animate-spin mr-2" size={20} />
                    Switching account...
                  </>
                ) : (
                  <>
                    <SignOut size={20} className="mr-2" />
                    Continue &amp; Switch Account
                  </>
                )}
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate("/")}
                className="text-zinc-500"
                disabled={switchingAccount}
                data-testid="cancel-switch-btn"
              >
                <XCircle size={16} className="mr-2" />
                Cancel
              </Button>
            </div>

            <p className="text-xs text-center text-zinc-400">
              Clicking &quot;Continue &amp; Switch Account&quot; will log you out and bring you back to this activation page.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Valid token - show password form
  return (
    <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-[#FF6600] rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-white" />
          </div>
          <CardTitle className="text-2xl font-bold">Set Up Your Account</CardTitle>
          <CardDescription>
            {tokenInfo?.email && (
              <span className="block mt-2">
                Creating account for <strong>{tokenInfo.email}</strong>
              </span>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="password">Create Password</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter a secure password"
                  className="pr-10"
                  required
                  minLength={8}
                  data-testid="activate-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400"
                >
                  {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <p className="text-xs text-zinc-500 mt-1">At least 8 characters</p>
            </div>

            <div>
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                className="mt-1"
                required
                data-testid="activate-confirm-password-input"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#FF6600] hover:bg-[#CC5200] text-white"
              data-testid="activate-submit-btn"
            >
              {submitting ? (
                <>
                  <SpinnerGap size={20} className="animate-spin mr-2" />
                  Activating...
                </>
              ) : (
                "Activate Account"
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
